/**
 * Wedding guest registry: Firestore when configured, else localStorage. Emails matched case-insensitively after trim.
 */

export const SITE_VISIBILITY = {
  CEREMONY_RECEPTION: 'ceremony_reception',
  /** Ceremony + reception with on-site accommodation copy (Stay section). */
  CEREMONY_RECEPTION_PENICUIK: 'ceremony_reception_penicuik',
  RECEPTION_ONLY: 'reception_only',
};

const SITE_VISIBILITY_SET = new Set(Object.values(SITE_VISIBILITY));

/** True for full-day invite variants (ceremony stack, hero, coach copy). */
export function isCeremonyPlusSiteVisibility(v) {
  return (
    v === SITE_VISIBILITY.CEREMONY_RECEPTION || v === SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK
  );
}

/**
 * Maps invite tier to Typeform hidden field `guest_type` (must match form variable names).
 * On-site Penicuik stays win over ceremony-only day guests.
 */
export function typeformGuestTypeFromSiteVisibility(siteVisibility) {
  if (!SITE_VISIBILITY_SET.has(siteVisibility)) return 'reception';
  if (siteVisibility === SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK) return 'penicuik';
  if (isCeremonyPlusSiteVisibility(siteVisibility)) return 'ceremony';
  return 'reception';
}

/**
 * Guest voicenote page with optional prefill and attribution (search params).
 * @param {string} voicenotePathname e.g. `getVoiceNotePathname()` from `adminPath.js`
 * @param {object | null | undefined} verifiedGuest
 * @param {Record<string, string>} [attribution] e.g. `{ source: 'faq_registry' }`
 */
export function buildGuestVoiceNoteHref(voicenotePathname, verifiedGuest, attribution = {}) {
  const path = String(voicenotePathname || '').trim() || '/voicenote';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(attribution)) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s) params.set(k, s);
  }
  if (verifiedGuest) {
    const fn = String(verifiedGuest.firstName ?? '').trim();
    const em = String(verifiedGuest.email ?? '').trim();
    if (fn) params.set('first_name', fn);
    if (em) params.set('email', em);
  }
  const q = params.toString();
  return q ? `${path}?${q}` : path;
}

/** Typeform prefill via URL fragment: #first_name=…&email=…&guest_type=… */
export function buildTypeformRsvpReplyHref(baseUrl, verifiedGuest) {
  const base = String(baseUrl || '')
    .trim()
    .replace(/#.*$/, '');
  if (!verifiedGuest || !base) return base || String(baseUrl || '').trim();
  const guestType = typeformGuestTypeFromSiteVisibility(verifiedGuest.siteVisibility);
  const firstName = verifiedGuest.firstName ?? '';
  const email = verifiedGuest.email ?? '';
  const hash = [
    `first_name=${encodeURIComponent(firstName)}`,
    `email=${encodeURIComponent(email)}`,
    `guest_type=${encodeURIComponent(guestType)}`,
  ].join('&');
  return `${base}#${hash}`;
}

export const RSVP_STATUSES = {
  NOT_SENT: 'not_sent',
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  DECLINED: 'declined',
};

/** Invited guests can access the site; backup = reserve list (no login until promoted). */
export const GUEST_POOL = {
  INVITED: 'invited',
  BACKUP: 'backup',
};

/** What they've told you verbally (admin tracking). */
export const VERBAL_RSVP = {
  UNSET: 'unset',
  YES: 'yes',
  NO: 'no',
  TENTATIVE: 'tentative',
};

/** Guest’s own RSVP submission on the site (Yes / Pending / No). */
export const FORM_RSVP = {
  PENDING: 'pending',
  YES: 'yes',
  NO: 'no',
};

/**
 * Admin-facing RSVP column: single status for dashboard + row picker.
 * Synced with formRsvp / verbalRsvp when changed in admin UI.
 */
export const ADMIN_RSVP = {
  NOT_INVITED: 'not_invited',
  DECLINED: 'declined',
  TENTATIVE: 'tentative',
  VERBAL_YES: 'verbal_yes',
  CONFIRMED: 'confirmed',
};

/** Short labels for selects and exports (admin sheet uses pill-style dropdown). */
export const ADMIN_RSVP_LABEL = {
  [ADMIN_RSVP.NOT_INVITED]: 'To invite',
  [ADMIN_RSVP.DECLINED]: 'Declined',
  [ADMIN_RSVP.TENTATIVE]: 'Tentative',
  [ADMIN_RSVP.VERBAL_YES]: 'Yes',
  [ADMIN_RSVP.CONFIRMED]: 'Confirmed',
};

const FORM_RSVP_SET = new Set(Object.values(FORM_RSVP));
const VERBAL_RSVP_SET = new Set(Object.values(VERBAL_RSVP));
const ADMIN_RSVP_SET = new Set(Object.values(ADMIN_RSVP));

/** Infer admin column from legacy form / verbal / rsvp when `adminRsvpStatus` not stored. */
export function adminRsvpFromFormAndVerbal(formRsvp, verbalRsvp, legacyRsvpStatus) {
  if (formRsvp === FORM_RSVP.YES) return ADMIN_RSVP.CONFIRMED;
  if (formRsvp === FORM_RSVP.NO) return ADMIN_RSVP.DECLINED;
  if (verbalRsvp === VERBAL_RSVP.NO) return ADMIN_RSVP.DECLINED;
  if (verbalRsvp === VERBAL_RSVP.YES) return ADMIN_RSVP.VERBAL_YES;
  if (verbalRsvp === VERBAL_RSVP.TENTATIVE) return ADMIN_RSVP.TENTATIVE;
  if (legacyRsvpStatus === RSVP_STATUSES.NOT_SENT) return ADMIN_RSVP.NOT_INVITED;
  return ADMIN_RSVP.TENTATIVE;
}

/** Keep form + verbal aligned when admin picks a status (form submission still wins on next load). */
export function formAndVerbalFromAdminRsvp(status) {
  switch (status) {
    case ADMIN_RSVP.CONFIRMED:
      return { formRsvp: FORM_RSVP.YES, verbalRsvp: VERBAL_RSVP.UNSET };
    case ADMIN_RSVP.DECLINED:
      return { formRsvp: FORM_RSVP.NO, verbalRsvp: VERBAL_RSVP.NO };
    case ADMIN_RSVP.VERBAL_YES:
      return { formRsvp: FORM_RSVP.PENDING, verbalRsvp: VERBAL_RSVP.YES };
    case ADMIN_RSVP.TENTATIVE:
      return { formRsvp: FORM_RSVP.PENDING, verbalRsvp: VERBAL_RSVP.TENTATIVE };
    case ADMIN_RSVP.NOT_INVITED:
      return { formRsvp: FORM_RSVP.PENDING, verbalRsvp: VERBAL_RSVP.UNSET };
    default:
      return { formRsvp: FORM_RSVP.PENDING, verbalRsvp: VERBAL_RSVP.UNSET };
  }
}

export function legacyFormRsvpFromRsvpStatus(rsvpStatus) {
  if (rsvpStatus === RSVP_STATUSES.CONFIRMED) return FORM_RSVP.YES;
  if (rsvpStatus === RSVP_STATUSES.DECLINED) return FORM_RSVP.NO;
  return FORM_RSVP.PENDING;
}

/** Maps form RSVP to legacy `rsvpStatus` for mailouts and older filters. */
export function computeRsvpStatusFromForm(formRsvp) {
  if (formRsvp === FORM_RSVP.YES) return RSVP_STATUSES.CONFIRMED;
  if (formRsvp === FORM_RSVP.NO) return RSVP_STATUSES.DECLINED;
  return RSVP_STATUSES.PENDING;
}

export function canGuestAccessSite(g) {
  if (!g) return false;
  const p = g.pool ?? GUEST_POOL.INVITED;
  return p !== GUEST_POOL.BACKUP;
}

export function sortGuestsForDisplay(guests) {
  return [...guests].sort((a, b) => {
    const emptyA = !(a.group || '').trim();
    const emptyB = !(b.group || '').trim();
    if (emptyA !== emptyB) return emptyA ? -1 : 1;
    const ga = (a.group || '').trim();
    const gb = (b.group || '').trim();
    if (ga !== gb) return ga.localeCompare(gb);
    const sa = a.sortOrder ?? 0;
    const sb = b.sortOrder ?? 0;
    if (sa !== sb) return sa - sb;
    const na = `${a.firstName || ''} ${a.lastName || ''}`.trim();
    const nb = `${b.firstName || ''} ${b.lastName || ''}`.trim();
    return na.localeCompare(nb);
  });
}

/**
 * Drop `draggedId` before `targetId`, optionally moving into target’s group. Renumbers sortOrder within that group.
 */
export function reorderGuestsAfterDrop(guests, draggedId, targetId) {
  const dragged = guests.find((g) => g.id === draggedId);
  const target = guests.find((g) => g.id === targetId);
  if (!dragged || !target || draggedId === targetId) return guests;

  const groupKey = (target.group || '').trim();
  const nextGroupValue = target.group || '';

  const withGroup = guests.map((g) => (g.id === draggedId ? { ...g, group: nextGroupValue } : g));

  const inGroupIds = sortGuestsForDisplay(
    withGroup.filter((g) => (g.group || '').trim() === groupKey),
  ).map((g) => g.id);

  const filtered = inGroupIds.filter((id) => id !== draggedId);
  const ti = filtered.indexOf(targetId);
  const insertAt = ti < 0 ? filtered.length : ti;
  const newIds = [...filtered.slice(0, insertAt), draggedId, ...filtered.slice(insertAt)];

  const orderById = new Map(newIds.map((id, i) => [id, i]));
  return withGroup.map((g) => {
    if (orderById.has(g.id) && (g.group || '').trim() === groupKey) {
      return { ...g, sortOrder: orderById.get(g.id) };
    }
    return g;
  });
}

export function setGroupLeadForGuest(guests, guestId, isLead) {
  const target = guests.find((x) => x.id === guestId);
  if (!target) return guests;
  const key = (target.group || '').trim();
  return guests.map((row) => {
    if ((row.group || '').trim() !== key) return row;
    if (row.id === guestId) return { ...row, isGroupLead: Boolean(isLead) };
    return isLead ? { ...row, isGroupLead: false } : row;
  });
}

/** Whose guest (invitation side); "joint" = both / shared list. */
export const GUEST_SIDE = {
  UNSPECIFIED: '',
  BEN: 'ben',
  MEG: 'meg',
  JOINT: 'joint',
};

export function normalizeGuestSide(raw) {
  const l = String(raw || '')
    .trim()
    .toLowerCase();
  if (!l) return GUEST_SIDE.UNSPECIFIED;
  if (l.includes('joint') || l.includes('both')) return GUEST_SIDE.JOINT;
  if (l.startsWith('ben')) return GUEST_SIDE.BEN;
  if (l.startsWith('meg')) return GUEST_SIDE.MEG;
  return GUEST_SIDE.UNSPECIFIED;
}

export const WEDDING_GUEST_REGISTRY_KEY = 'weddingGuestRegistryV1';
export const WEDDING_GUEST_REGISTRY_CHANGE_EVENT = 'weddingGuestRegistryChange';
/** detail: {{ message: string, code?: string }} — cloud save failed; list may only exist in this browser. */
export const WEDDING_GUEST_REGISTRY_SAVE_FAILED_EVENT = 'weddingGuestRegistrySaveFailed';
/** Fired after a successful Firestore write of the guest registry (this tab only). */
export const WEDDING_GUEST_REGISTRY_PERSISTED_OK_EVENT = 'weddingGuestRegistryPersistedOk';
/**
 * When set in sessionStorage to `'1'`, the next cloud save may reduce the guest count sharply (bulk delete /
 * replace). Cleared automatically after a successful Firestore write. See admin checkbox.
 */
export const GUEST_REGISTRY_SHRINK_GUARD_BYPASS_SESSION_KEY = 'weddingGuestShrinkGuardBypass';

export function setGuestRegistryShrinkGuardBypass(enabled) {
  if (typeof sessionStorage === 'undefined') return;
  if (enabled) {
    sessionStorage.setItem(GUEST_REGISTRY_SHRINK_GUARD_BYPASS_SESSION_KEY, '1');
  } else {
    sessionStorage.removeItem(GUEST_REGISTRY_SHRINK_GUARD_BYPASS_SESSION_KEY);
  }
}

function guestRegistrySaveFailedDetail(err) {
  const code = err && typeof err === 'object' ? err.code : '';
  const msg = err && typeof err === 'object' && err.message ? String(err.message) : String(err || 'Unknown error');
  if (msg === 'guest-registry-stale-baseline') {
    return {
      code: 'stale-baseline',
      message:
        'The guest list changed in Firestore since this tab loaded (or this page is an old cached copy). Reload the page, then try saving again.',
    };
  }
  if (msg === 'guest-registry-empty-wipe-guard') {
    return {
      code: 'empty-wipe-guard',
      message:
        'Save blocked: this browser tried to write an empty guest list to Firestore while the cloud registry still has data. The cloud was not changed. Reload the admin page if the sheet should show guests; your edits stay in this browser only until you fix the view.',
    };
  }
  if (code === 'permission-denied') {
    return {
      code,
      message:
        'Firestore blocked the save (permission denied). Confirm rules and config/registryAdmins access, then try again.',
    };
  }
  if (code === 'unavailable' || code === 'deadline-exceeded') {
    return { code, message: `Network or server unavailable: ${msg}` };
  }
  if (
    /exceed|too large|document.*too large|maximum entity|1\s*mi?b/i.test(msg) ||
    (code === 'invalid-argument' && /size|limit|too large|1\s*mi?b/i.test(msg))
  ) {
    return {
      code: code || 'size',
      message:
        'Save may exceed Firestore’s per-document size limit (~1 MiB). Export a backup, trim long fields, or split the list.',
    };
  }
  return { code: code || undefined, message: msg };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Extra seats counted on this invitation (e.g. partner, children) — vendor headcount often uses 1 + plusOnes. */
export function normalizePlusOnes(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(50, Math.floor(n));
}

/** Coach party size from RSVP form (primary guest); undefined = not specified / use default weight of 1 in totals. */
export function normalizeCoachSeatCount(raw) {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n)) return undefined;
  const i = Math.floor(n);
  if (i < 1 || i > 50) return undefined;
  return i;
}

/** Coach return time (null = not set). */
export const COACH_OUT_1030 = '10:30';
export const COACH_OUT_1230 = '12:30';

export function normalizeCoachOut(v) {
  if (v === COACH_OUT_1030 || v === '10.30' || v === '1030') return COACH_OUT_1030;
  if (v === COACH_OUT_1230 || v === '12.30' || v === '1230') return COACH_OUT_1230;
  return null;
}

/** Cycle: unset → 10:30 → 12:30 → unset. */
export function nextCoachOutValue(current) {
  if (current == null || current === undefined) return COACH_OUT_1030;
  if (current === COACH_OUT_1030) return COACH_OUT_1230;
  return null;
}

export function normalizeCoachIn(v) {
  if (v === true || v === 'yes' || v === 'y' || v === 'true' || v === '1') return true;
  return null;
}

/**
 * Default tag labels for the admin palette (emoji + text). Applied to guests as stored strings.
 * Admins can add more via the guest list UI (saved in localStorage).
 */
export const DEFAULT_TAG_PRESETS = Object.freeze([
  '💒 Wedding party',
  '🤵 Groomsmen',
  '👰 Bridesmaids',
  '✈️ Long Haul',
]);

/** Free-form labels (e.g. “Wedding party”, “Out of town”); deduped case-insensitively, order preserved. */
export function normalizeTags(raw) {
  if (raw == null) return [];
  let parts = [];
  if (Array.isArray(raw)) {
    parts = raw.map((t) => String(t).trim()).filter(Boolean);
  } else if (typeof raw === 'string') {
    parts = raw.split(/[,;|]/).map((t) => t.trim()).filter(Boolean);
  }
  const seen = new Set();
  const out = [];
  for (const t of parts) {
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t.slice(0, 64));
    if (out.length >= 24) break;
  }
  return out;
}

/** True when text is almost certainly a postal address, not a household name. */
export function looksLikePostalAddress(raw) {
  const t = String(raw || '').trim();
  if (!t) return false;
  if (t.includes('\n') || t.includes('\r')) return true;
  if (/\b[A-Z]{1,2}\d[A-Z0-9]?\s+\d[A-Z]{2}\b/i.test(t)) return true;
  if (/\b\d{5}(-\d{4})?\b/.test(t) && t.length > 12) return true;
  if (t.length > 35 && /\d/.test(t) && (t.includes(',') || t.includes('·'))) return true;
  return false;
}

export function isValidGuestRow(g) {
  if (
    !g ||
    typeof g.id !== 'string' ||
    typeof g.email !== 'string' ||
    typeof g.firstName !== 'string' ||
    typeof g.group !== 'string' ||
    !SITE_VISIBILITY_SET.has(g.siteVisibility) ||
    !Object.values(RSVP_STATUSES).includes(g.rsvpStatus) ||
    !(g.rsvpUpdatedAt === null || typeof g.rsvpUpdatedAt === 'string')
  ) {
    return false;
  }
  if (typeof g.lastName !== 'string') return false;
  if (!FORM_RSVP_SET.has(g.formRsvp)) return false;
  if (!VERBAL_RSVP_SET.has(g.verbalRsvp)) return false;
  if (!ADMIN_RSVP_SET.has(g.adminRsvpStatus)) return false;
  const pool = g.pool ?? GUEST_POOL.INVITED;
  if (pool !== GUEST_POOL.INVITED && pool !== GUEST_POOL.BACKUP) return false;
  for (const k of ['dietary', 'notes', 'address', 'phone', 'accommodation']) {
    if (g[k] !== undefined && g[k] !== null && typeof g[k] !== 'string') return false;
  }
  if (!(g.coachIn === undefined || g.coachIn === null || g.coachIn === true)) return false;
  if (
    !(
      g.coachOut === undefined ||
      g.coachOut === null ||
      g.coachOut === COACH_OUT_1030 ||
      g.coachOut === COACH_OUT_1230
    )
  ) {
    return false;
  }
  if (g.isGroupLead !== undefined && typeof g.isGroupLead !== 'boolean') return false;
  if (g.sortOrder !== undefined && typeof g.sortOrder !== 'number') return false;
  const plusOnes = g.plusOnes ?? 0;
  if (typeof plusOnes !== 'number' || !Number.isInteger(plusOnes) || plusOnes < 0 || plusOnes > 50) {
    return false;
  }
  if (g.coachSeatCount !== undefined && g.coachSeatCount !== null) {
    if (typeof g.coachSeatCount !== 'number' || !Number.isInteger(g.coachSeatCount)) return false;
    if (g.coachSeatCount < 1 || g.coachSeatCount > 50) return false;
  }
  if (
    g.trainTravelDec29 !== undefined &&
    g.trainTravelDec29 !== null &&
    typeof g.trainTravelDec29 !== 'boolean'
  ) {
    return false;
  }
  if (
    g.inviteUrlSent !== undefined &&
    g.inviteUrlSent !== null &&
    typeof g.inviteUrlSent !== 'boolean'
  ) {
    return false;
  }
  if (!Array.isArray(g.tags)) return false;
  if (g.tags.length > 24) return false;
  for (const t of g.tags) {
    if (typeof t !== 'string' || t.length > 64) return false;
  }
  return true;
}

/**
 * Ensures extended fields exist and syncs `formRsvp` ↔ legacy `rsvpStatus`.
 * Call on every row after load (localStorage / Firestore).
 */
/** Firestore / Typeform rows often omit fields; isValidGuestRow requires null|string for this. */
function normalizeRsvpUpdatedAt(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw.toDate === 'function') {
    try {
      return raw.toDate().toISOString();
    } catch {
      return null;
    }
  }
  return null;
}

export function normalizeGuestRow(g) {
  if (g == null || typeof g !== 'object' || Array.isArray(g)) {
    return null;
  }
  const { side: legacySide } = g;
  const idRaw = g?.id;
  const id = typeof idRaw === 'string' ? idRaw : idRaw != null ? String(idRaw) : '';
  const lastName = typeof g.lastName === 'string' ? g.lastName : '';
  const firstName = typeof g.firstName === 'string' ? g.firstName : '';
  const group = typeof g.group === 'string' ? g.group : '';
  const pool = g.pool === GUEST_POOL.BACKUP ? GUEST_POOL.BACKUP : GUEST_POOL.INVITED;
  const legacyRsvpStatus = Object.values(RSVP_STATUSES).includes(g.rsvpStatus) ? g.rsvpStatus : RSVP_STATUSES.PENDING;
  let formRsvp = g.formRsvp;
  if (!FORM_RSVP_SET.has(formRsvp)) {
    formRsvp = legacyFormRsvpFromRsvpStatus(legacyRsvpStatus);
  }
  const verbalRsvp = VERBAL_RSVP_SET.has(g.verbalRsvp) ? g.verbalRsvp : VERBAL_RSVP.UNSET;
  let adminRsvpStatus;
  if (formRsvp === FORM_RSVP.YES) adminRsvpStatus = ADMIN_RSVP.CONFIRMED;
  else if (formRsvp === FORM_RSVP.NO) adminRsvpStatus = ADMIN_RSVP.DECLINED;
  else if (ADMIN_RSVP_SET.has(g.adminRsvpStatus)) adminRsvpStatus = g.adminRsvpStatus;
  else adminRsvpStatus = adminRsvpFromFormAndVerbal(formRsvp, verbalRsvp, legacyRsvpStatus);
  const rsvpStatus = computeRsvpStatusFromForm(formRsvp);
  const isGroupLead = Boolean(g.isGroupLead);
  const sortOrder = typeof g.sortOrder === 'number' && !Number.isNaN(g.sortOrder) ? g.sortOrder : 0;
  const siteVisibility = SITE_VISIBILITY_SET.has(g.siteVisibility)
    ? g.siteVisibility
    : SITE_VISIBILITY.RECEPTION_ONLY;
  const email = normalizeEmail(typeof g.email === 'string' ? g.email : '');

  /** Only known fields — avoids Firestore rejects (undefined/extra keys) and runaway document size. */
  const row = {
    id,
    email,
    firstName,
    group,
    siteVisibility,
    rsvpUpdatedAt: normalizeRsvpUpdatedAt(g.rsvpUpdatedAt),
    guestSide: normalizeGuestSide(g.guestSide ?? legacySide),
    lastName,
    dietary: typeof g.dietary === 'string' ? g.dietary : '',
    notes: typeof g.notes === 'string' ? g.notes : '',
    address: typeof g.address === 'string' ? g.address : '',
    phone: typeof g.phone === 'string' ? g.phone : '',
    accommodation: typeof g.accommodation === 'string' ? g.accommodation : '',
    coachIn: normalizeCoachIn(g.coachIn),
    coachOut: normalizeCoachOut(g.coachOut),
    pool,
    verbalRsvp,
    formRsvp,
    adminRsvpStatus,
    isGroupLead,
    sortOrder,
    plusOnes: normalizePlusOnes(g.plusOnes),
    tags: normalizeTags(g.tags),
    rsvpStatus,
  };
  const coachSeatCount = normalizeCoachSeatCount(g.coachSeatCount);
  if (coachSeatCount !== undefined) {
    row.coachSeatCount = coachSeatCount;
  }
  if (g.trainTravelDec29 === true || g.trainTravelDec29 === false) {
    row.trainTravelDec29 = g.trainTravelDec29 === true;
  }
  if (g.inviteUrlSent === true || g.inviteUrlSent === false) {
    row.inviteUrlSent = g.inviteUrlSent === true;
  }
  return row;
}

export function normalizeEmail(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase();
}

/** Firebase Firestore live registry (optional). When configured, snapshot is authoritative after first sync. */
let remoteGuestList = null;
let remoteDocExists = false;
let remoteRegistryHydrated = false;
/** Latest `updatedAt` from Firestore for the registry doc (optimistic locking / stale-tab guard). */
let remoteRegistryDocUpdatedAt = null;

const SHRINK_GUARD_MIN_PRIOR = 20;
const SHRINK_GUARD_MAX_DROP_ABS = 15;
const SHRINK_GUARD_MAX_DROP_FRAC = 0.25;

function shrinkGuardWouldBlock(priorLen, nextLen, bypass) {
  if (bypass) return false;
  if (priorLen == null || priorLen < SHRINK_GUARD_MIN_PRIOR) return false;
  if (nextLen >= priorLen) return false;
  const maxDrop = Math.max(SHRINK_GUARD_MAX_DROP_ABS, Math.ceil(priorLen * SHRINK_GUARD_MAX_DROP_FRAC));
  return nextLen < priorLen - maxDrop;
}

export function isRemoteGuestRegistryConfigured() {
  return Boolean(
    process.env.REACT_APP_FIREBASE_API_KEY && process.env.REACT_APP_FIREBASE_PROJECT_ID,
  );
}

/**
 * Called from firebaseRemoteSync when Firestore snapshot arrives.
 * @param {{ guests: unknown[]|null, docExists: boolean, hydrated: boolean, serverUpdatedAt?: import('firebase/firestore').Timestamp | null }} state
 */
export function applyRemoteGuestRegistrySnapshot(state) {
  remoteRegistryHydrated = state.hydrated;
  remoteDocExists = state.docExists;
  remoteGuestList = state.guests;
  if (!state.docExists) {
    remoteRegistryDocUpdatedAt = null;
  } else if (Object.prototype.hasOwnProperty.call(state, 'serverUpdatedAt')) {
    remoteRegistryDocUpdatedAt = state.serverUpdatedAt ?? null;
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(WEDDING_GUEST_REGISTRY_CHANGE_EVENT));
  }
}

/** Firestore `updatedAt` this tab last saw on the registry doc (for conditional writes). */
export function getRemoteRegistryWriteBaseline() {
  return remoteRegistryDocUpdatedAt;
}

export function getRemoteRegistryDocExistsForWriteGuard() {
  return remoteDocExists;
}

/** After a successful cloud save, align baseline with the committed `updatedAt` (before onSnapshot arrives). */
export function ackRemoteRegistryDocUpdatedAt(ts) {
  if (ts != null && typeof ts.toMillis === 'function' && typeof ts.isEqual === 'function') {
    remoteRegistryDocUpdatedAt = ts;
  }
}

/**
 * Firestore listen error — do **not** clear last good snapshot (the old onSnapshot onError wiped the whole list).
 */
export function applyRemoteGuestRegistryListenError() {
  remoteRegistryHydrated = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(WEDDING_GUEST_REGISTRY_CHANGE_EVENT));
  }
}

/** Firestore `guests` is usually an array; tolerate a plain object map. */
export function coerceFirestoreGuestsToArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw != null && typeof raw === 'object') {
    return Object.values(raw);
  }
  return [];
}

export function isRemoteGuestRegistryHydrated() {
  return remoteRegistryHydrated;
}

/** Browser-only backup (same key as offline saves). Export / restore from admin if cloud diverges. */
export function readGuestRegistryLocalStorageDraft() {
  return getLocalGuestRegistryRemoteFallback();
}

/** Parsed localStorage draft only (no seed). Used when cloud snapshot is empty/unreachable. */
function getLocalGuestRegistryRemoteFallback() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(WEDDING_GUEST_REGISTRY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const normalized = parsed.map(normalizeGuestRow).filter(Boolean);
    const valid = normalized.filter(isValidGuestRow);
    if (normalized.length === 0) return [];
    if (valid.length === 0) return normalized;
    if (valid.length === normalized.length) return valid;
    return normalized;
  } catch {
    return [];
  }
}

function getLocalGuestRegistry() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(WEDDING_GUEST_REGISTRY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const normalized = parsed.map(normalizeGuestRow).filter(Boolean);
    const valid = normalized.filter(isValidGuestRow);
    if (!normalized.length) return [];
    if (!valid.length) return normalized;
    if (valid.length === normalized.length) return valid;
    return normalized;
  } catch {
    return [];
  }
}

export function isValidEmailFormat(raw) {
  return EMAIL_RE.test(normalizeEmail(raw));
}

/**
 * Guest list for the email gate and site copy. With Firebase env vars, uses the Firestore snapshot after sync.
 * Rows are normalized. When every row fails strict validation (schema drift), still return normalized rows so the
 * UI doesn’t go blank. When only some rows fail validation, return the full normalized list so we don’t hide rows
 * or encourage saves that truncate Firestore.
 */
export function getGuestRegistry() {
  if (isRemoteGuestRegistryConfigured()) {
    if (!remoteRegistryHydrated) {
      return [];
    }
    if (remoteDocExists && Array.isArray(remoteGuestList)) {
      const normalized = remoteGuestList
        .filter((row) => row != null && typeof row === 'object' && !Array.isArray(row))
        .map(normalizeGuestRow)
        .filter(Boolean);
      if (normalized.length > 0) {
        const valid = normalized.filter(isValidGuestRow);
        // If every row fails strict validation (schema drift), still show normalized so the UI isn’t empty.
        // If only *some* fail, still return the full normalized list — otherwise we “lose” rows in the admin
        // and the next save would persist only the valid subset and truncate Firestore.
        if (valid.length === 0) return normalized;
        if (valid.length === normalized.length) return valid;
        return normalized;
      }
    }
    const localDraft = getLocalGuestRegistryRemoteFallback();
    if (localDraft.length > 0) {
      return localDraft;
    }
    return [];
  }
  return getLocalGuestRegistry();
}

function persistLocalGuestRegistry(guests) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(WEDDING_GUEST_REGISTRY_KEY, JSON.stringify(guests));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(WEDDING_GUEST_REGISTRY_CHANGE_EVENT));
  }
}

/**
 * Persists registry. When Firestore is configured and you are signed in (production admin), writes cloud first;
 * onSnapshot updates everyone immediately. Otherwise uses localStorage (dev / offline).
 * @returns {void|Promise<void>}
 */
export function saveGuestRegistry(guests) {
  if (isRemoteGuestRegistryConfigured()) {
    return import('./firebaseRemoteSync')
      .then((mod) => {
        if (mod.isFirebaseSignedIn()) {
          const normalized = guests.map(normalizeGuestRow).filter(Boolean);
          // Never persist only the strict-valid subset: a partially-valid list would replace the full document
          // and delete everyone who fails isValidGuestRow (e.g. legacy/import field shapes).
          const toWrite = normalized.length > 0 ? normalized : guests;
          const bypass =
            typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem(GUEST_REGISTRY_SHRINK_GUARD_BYPASS_SESSION_KEY) === '1';
          const priorLen =
            remoteRegistryHydrated && remoteDocExists && Array.isArray(remoteGuestList)
              ? remoteGuestList.length
              : null;
          /**
           * Never replace a non-empty Firestore registry with [] from this client (bad hydration / local bug /
           * shrink-guard only applied when priorLen ≥ 20). Creating an empty doc when no doc exists is still allowed.
           */
          const emptyWriteWouldWipeCloud =
            toWrite.length === 0 &&
            remoteRegistryHydrated &&
            remoteDocExists &&
            (priorLen == null || priorLen > 0);
          if (emptyWriteWouldWipeCloud) {
            persistLocalGuestRegistry(guests);
            const msg =
              'Save blocked: Firestore still has a guest list, but this save would write zero rows. The cloud was not changed — reload the admin page or use “browser backup” if the sheet wrongly looks empty.';
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent(WEDDING_GUEST_REGISTRY_SAVE_FAILED_EVENT, {
                  detail: { message: msg, code: 'empty-wipe-guard' },
                }),
              );
            }
            throw new Error('guest-registry-empty-wipe-guard');
          }
          if (shrinkGuardWouldBlock(priorLen, toWrite.length, bypass)) {
            persistLocalGuestRegistry(guests);
            const msg = `Save blocked: Firestore has ${priorLen} guests but this save would write ${toWrite.length}. Nothing was changed in the cloud — your edits are only in this browser (localStorage). Export a CSV backup. If you really are removing a large chunk of the list, turn on “Allow large guest-list reductions” under Guest list, then save again.`;
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent(WEDDING_GUEST_REGISTRY_SAVE_FAILED_EVENT, {
                  detail: { message: msg, code: 'shrink-guard' },
                }),
              );
            }
            throw new Error('guest-registry-shrink-guard');
          }
          // Avoid clobbering cloud with [] before the first onSnapshot (e.g. React Strict Mode unmount flush).
          if (toWrite.length === 0 && !remoteRegistryHydrated) {
            return undefined;
          }
          return mod.persistRemoteGuests(toWrite).then(() => {
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.removeItem(GUEST_REGISTRY_SHRINK_GUARD_BYPASS_SESSION_KEY);
            }
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent(WEDDING_GUEST_REGISTRY_PERSISTED_OK_EVENT));
            }
          });
        }
        persistLocalGuestRegistry(guests);
        const msg =
          'Not signed in to Firebase — guest list was saved only in this browser. Sign in on /admin to sync to Firestore.';
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(WEDDING_GUEST_REGISTRY_SAVE_FAILED_EVENT, {
              detail: { message: msg, code: 'not-signed-in' },
            }),
          );
        }
        return undefined;
      })
      .catch((err) => {
        if (err && err.message === 'guest-registry-shrink-guard') {
          throw err;
        }
        if (err && err.message === 'guest-registry-empty-wipe-guard') {
          throw err;
        }
        // Stale tab / concurrent edit: cloud was not updated — do not overwrite a larger local backup with this payload.
        if (err && err.message === 'guest-registry-stale-baseline') {
          const detail = guestRegistrySaveFailedDetail(err);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent(WEDDING_GUEST_REGISTRY_SAVE_FAILED_EVENT, { detail }),
            );
          }
          throw err;
        }
        persistLocalGuestRegistry(guests);
        const detail = guestRegistrySaveFailedDetail(err);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(WEDDING_GUEST_REGISTRY_SAVE_FAILED_EVENT, { detail }),
          );
        }
        throw err;
      });
  }

  persistLocalGuestRegistry(guests);
  return undefined;
}

export function subscribeGuestRegistry(callback) {
  if (typeof window === 'undefined') return () => {};
  const handler = () => {
    callback();
  };
  queueMicrotask(handler);
  window.addEventListener(WEDDING_GUEST_REGISTRY_CHANGE_EVENT, handler);
  return () => window.removeEventListener(WEDDING_GUEST_REGISTRY_CHANGE_EVENT, handler);
}

export function findGuestByEmail(raw) {
  const n = normalizeEmail(raw);
  if (!n) return null;
  return getGuestRegistry().find((g) => g.email.toLowerCase() === n) ?? null;
}

export const ACCESS_STORAGE_KEY = 'weddingGuestVerified';

/** Development only: 'auto' follows the signed-in guest; otherwise forces site variant. */
export const DEV_SITE_VISIBILITY_MODE_KEY = 'weddingDevSiteVisibilityMode';

export function readVerifiedGuestFromStorage() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(ACCESS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const email = typeof parsed?.email === 'string' ? parsed.email : '';
    if (!email) return null;

    // While the Firestore snapshot is still loading, getGuestRegistry() is intentionally empty — don’t treat
    // that as “guest removed” or we clear valid sessions on every hard refresh.
    if (isRemoteGuestRegistryConfigured() && !isRemoteGuestRegistryHydrated()) {
      const firstName = typeof parsed.firstName === 'string' ? parsed.firstName : '';
      const id = typeof parsed.id === 'string' ? parsed.id : '';
      const sv = parsed.siteVisibility;
      const siteVisibility = SITE_VISIBILITY_SET.has(sv) ? sv : SITE_VISIBILITY.RECEPTION_ONLY;
      return {
        email: normalizeEmail(email),
        firstName,
        id,
        siteVisibility,
      };
    }

    const guest = findGuestByEmail(email);
    if (!guest) {
      sessionStorage.removeItem(ACCESS_STORAGE_KEY);
      return null;
    }
    return {
      email: normalizeEmail(email),
      firstName: guest.firstName,
      id: guest.id,
      siteVisibility: guest.siteVisibility,
    };
  } catch {
    sessionStorage.removeItem(ACCESS_STORAGE_KEY);
    return null;
  }
}

export function persistVerifiedGuest(email) {
  const guest = findGuestByEmail(email);
  if (!guest || typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(
    ACCESS_STORAGE_KEY,
    JSON.stringify({
      email: normalizeEmail(email),
      firstName: guest.firstName,
      id: guest.id,
      siteVisibility: guest.siteVisibility,
    }),
  );
}

export function clearVerifiedGuestStorage() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(ACCESS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function readDevSiteVisibilityMode() {
  if (typeof localStorage === 'undefined') return 'auto';
  try {
    const v = localStorage.getItem(DEV_SITE_VISIBILITY_MODE_KEY);
    if (
      v === SITE_VISIBILITY.CEREMONY_RECEPTION ||
      v === SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK ||
      v === SITE_VISIBILITY.RECEPTION_ONLY
    ) {
      return v;
    }
  } catch {
    /* ignore */
  }
  return 'auto';
}

export function writeDevSiteVisibilityMode(mode) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (mode === 'auto') localStorage.removeItem(DEV_SITE_VISIBILITY_MODE_KEY);
    else if (
      mode === SITE_VISIBILITY.CEREMONY_RECEPTION ||
      mode === SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK ||
      mode === SITE_VISIBILITY.RECEPTION_ONLY
    ) {
      localStorage.setItem(DEV_SITE_VISIBILITY_MODE_KEY, mode);
    }
  } catch {
    /* ignore */
  }
}

/** Signed-in guest registry row decides production; in dev, optional forced preview mode. */
export function resolveEffectiveSiteVisibility(isDev, devMode, verifiedGuest) {
  if (
    isDev &&
    devMode !== 'auto' &&
    (devMode === SITE_VISIBILITY.CEREMONY_RECEPTION ||
      devMode === SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK ||
      devMode === SITE_VISIBILITY.RECEPTION_ONLY)
  ) {
    return devMode;
  }
  return verifiedGuest?.siteVisibility ?? SITE_VISIBILITY.RECEPTION_ONLY;
}

/** Development: persist first registry guest so refresh keeps the main site open. */
export function persistDevBypassGuest() {
  const reg = getGuestRegistry();
  const g = reg[0];
  if (!g) return;
  persistVerifiedGuest(g.email);
}
